import React from "react";
import { useTranslation } from "react-i18next";

import {
  getAvailableLanguagesForLocale,
  getLanguageOption,
  getPreferredEnglishLanguage,
  type LanguageOption,
  resolvePreferredLanguage,
  USER_LANGUAGE_STORAGE_KEY,
} from "@/i18n/languageConfig";

const FALLBACK_FLAG_BY_LANGUAGE_CODE: Record<string, string> = {
  ar: "/images/flags/ar.svg",
  ca: "/images/flags/ca.svg",
  de: "/images/flags/de.svg",
  en: "/images/flags/en_us.svg",
  "en-GB": "/images/flags/en_gb.svg",
  es: "/images/flags/es.svg",
  "es-419": "/images/flags/es.svg",
  fr: "/images/flags/fr.svg",
  "fr-CA": "/images/flags/fr_ca.svg",
  it: "/images/flags/it.svg",
  nl: "/images/flags/nl.svg",
  zh: "/images/flags/zh.svg",
};

const LOCALE_SPECIFIC_FLAGS: Record<string, Partial<Record<string, string>>> = {
  "ar-AE": { ar: "/images/flags/ar_ae.svg" },
  "ar-BH": { ar: "/images/flags/ar_bh.svg" },
  "ar-EG": { ar: "/images/flags/ar_eg.svg" },
  "ar-JO": { ar: "/images/flags/ar_jo.svg" },
  "ar-KW": { ar: "/images/flags/ar_kw.svg" },
  "ar-QA": { ar: "/images/flags/ar_qa.svg" },
  "ar-SA": { ar: "/images/flags/ar_sa.svg" },
  "en-AU": { en: "/images/flags/en_au.svg" },
  "en-CA": {
    en: "/images/flags/en_ca.svg",
    fr: "/images/flags/fr_ca.svg",
  },
  "en-GB": { en: "/images/flags/en_gb.svg" },
  "en-IE": { en: "/images/flags/en_ie.svg" },
  "en-ZA": { en: "/images/flags/en_za.svg" },
  "es-PR": { es: "/images/flags/en_pr.svg" },
  "fr-CA": {
    en: "/images/flags/en_ca.svg",
    fr: "/images/flags/fr_ca.svg",
  },
};

const getBrowserLocale = () => {
  if (typeof navigator === "undefined") {
    return "en-US";
  }

  return navigator.languages?.[0] ?? navigator.language ?? "en-US";
};

export const getFlagPath = (option: LanguageOption, locale: string) => {
  const localeFlag = LOCALE_SPECIFIC_FLAGS[locale]?.[option.baseCode];
  if (localeFlag) {
    return localeFlag;
  }

  if (option.code === "en") {
    return FALLBACK_FLAG_BY_LANGUAGE_CODE[getPreferredEnglishLanguage(locale)];
  }

  return (
    FALLBACK_FLAG_BY_LANGUAGE_CODE[option.code] ?? FALLBACK_FLAG_BY_LANGUAGE_CODE[option.baseCode]
  );
};

const getLanguageOptionLabel = (option: LanguageOption, duplicateDisplays: ReadonlySet<string>) => {
  if (duplicateDisplays.has(option.display) && option.regionLabel) {
    return `${option.display} (${option.regionLabel})`;
  }

  return option.display;
};

export const useLanguagePickerModel = () => {
  const { i18n } = useTranslation();

  const browserLocale = getBrowserLocale();
  const currentLanguageCode = i18n.resolvedLanguage ?? i18n.language;
  const currentLanguage = getLanguageOption(currentLanguageCode) ?? getLanguageOption("en");
  const detectedLanguageCode = resolvePreferredLanguage(browserLocale);
  const detectedLanguage = getLanguageOption(detectedLanguageCode) ?? currentLanguage;

  const availableLanguages = React.useMemo(() => {
    const options = getAvailableLanguagesForLocale(browserLocale);
    const orderedOptions: LanguageOption[] = [];
    const optionBaseCodes = new Set(options.map((option) => option.baseCode));

    if (detectedLanguage) {
      orderedOptions.push(detectedLanguage);
    }

    if (
      currentLanguage &&
      !orderedOptions.some((option) => option.code === currentLanguage.code) &&
      !optionBaseCodes.has(currentLanguage.baseCode)
    ) {
      orderedOptions.push(currentLanguage);
    }

    for (const option of options) {
      if (!orderedOptions.some((existingOption) => existingOption.code === option.code)) {
        orderedOptions.push(option);
      }
    }

    return orderedOptions;
  }, [browserLocale, currentLanguage, detectedLanguage]);

  const duplicateDisplays = React.useMemo(() => {
    const counts = new Map<string, number>();

    for (const option of availableLanguages) {
      counts.set(option.display, (counts.get(option.display) ?? 0) + 1);
    }

    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([display]) => display),
    );
  }, [availableLanguages]);

  const getOptionLabel = React.useCallback(
    (option: LanguageOption) => getLanguageOptionLabel(option, duplicateDisplays),
    [duplicateDisplays],
  );

  const changeLanguage = React.useCallback(
    async (code: string) => {
      if (code !== currentLanguageCode) {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, code);
        }
        await i18n.changeLanguage(code);
      }
    },
    [currentLanguageCode, i18n],
  );

  return {
    availableLanguages,
    browserLocale,
    changeLanguage,
    currentLanguage,
    currentLanguageCode,
    getOptionLabel,
  };
};
